import { useEffect, useState, useCallback } from 'react';
import api from '../config/axios';

/**
 * Hook لـ Secure Image URLs مع Token Authentication
 * يدعم thumbnails, avatars, والصور الأخرى المحمية
 */
export function useSecureImageUrl(imageType, entityId, enabled = true) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imagePath, setImagePath] = useState(null);

  const fetchImageUrl = useCallback(async () => {
    if (!enabled || !entityId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // مثال: baseURL = "http://localhost:3000/api"
      // لذلك لا نضيف /api/ في البداية
      const endpoint = imageType === 'avatar'
        ? `/users/user/${entityId}/avatar-url`
        : imageType === 'lesson'
          ? `/lessons/${entityId}/image-url`
          : `/lessons/course/${entityId}/image-url`;
      
      const response = await api.get(endpoint);

      if (response.data && response.data.success && response.data.data && response.data.data.imageUrl) {
        setImageUrl(response.data.data.imageUrl);
        setImagePath(response.data.data.imagePath);
      } else {
        const issue = !response.data?.success ? 'فشل الطلب' 
                    : !response.data?.data ? 'بيانات فارغة' 
                    : !response.data?.data?.imageUrl ? 'لا يوجد رابط صورة'
                    : 'خطأ غير معروف';
        setError(issue);
      }
    } catch (err) {
      const status = err.response?.status;
      
      // تحديد نوع الخطأ ورسالة مناسبة
      let userMessage = 'فشل جلب رابط الصورة';
      if (status === 404) {
        userMessage = 'الدرس غير موجود';
      } else if (status === 403) {
        userMessage = 'لا توجد صلاحية للوصول إلى هذه الصورة';
      } else if (status === 500) {
        userMessage = 'خطأ في الخادم';
      } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network')) {
        userMessage = 'فشل الاتصال بالخادم';
      }
      
      // Silently handle errors in production
      
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  }, [imageType, entityId, enabled]);

  useEffect(() => {
    fetchImageUrl();
  }, [fetchImageUrl]);

  return { imageUrl, imagePath, loading, error, refetch: fetchImageUrl };
}

/**
 * Hook للصور مع معلومات إضافية
 */
export function useImage(imageType, entityId, enabled = true) {
  const { imageUrl, imagePath, loading, error } = useSecureImageUrl(imageType, entityId, enabled);
  const [imageExists, setImageExists] = useState(false);

  useEffect(() => {
    setImageExists(!!imageUrl);
  }, [imageUrl]);

  return {
    imageUrl,
    imagePath,
    loading,
    error,
    imageExists,
    isLoading: loading,
    hasError: !!error,
    isEmpty: !imageUrl && !loading
  };
}

/**
 * Hook لـ Course Thumbnail مع Fallback
 */
export function useCourseThumbnail(courseId) {
  const { imageUrl, error } = useSecureImageUrl('course', courseId);

  return {
    thumbnailUrl: imageUrl,
    error,
    hasThumbnail: !!imageUrl
  };
}

/**
 * Hook لـ Lesson Thumbnail مع Fallback
 * ✅ يستخدم ImageType = 'lesson' بشكل صريح
 */
export function useLessonThumbnail(lessonId) {
  const { imageUrl, error, loading } = useSecureImageUrl('lesson', lessonId);
  return {
    thumbnailUrl: imageUrl,      // ✅ واضح: صورة مصغرة للدرس
    loading,
    error,
    hasThumbnail: !!imageUrl     // ✅ واضح: هل توجد صورة مصغرة
  };
}

export default useSecureImageUrl;
